/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1903427592 — ContactPage */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { ContentBox, PrimaryButton, ScreenTitle, ScreenWrapper } from '../../../../shared/ui';
import { useAppStore } from '../../../../shared/hooks/useAppStore';
import { debug } from '../../../../shared/lib/general';
import { CustomTextField } from '../../components/CustomTextField';
import styles from '../../components/FeedbacksStyles.module.css';
import {
    COMPANY_IDS_FOR_FEEDBACKS,
    DISABLE_OR_ENABLE_BUTTON,
    FEEDBACKS_CUSTOMER_STATUS,
    FEEDBACK_ROUTES,
} from '../../feedback.constants';
import { UpdateFeedback } from '../../feedback.types';
import { useFeedbackStore } from '../../hooks/useFeedbackStore';
import { useUpdateFeedbacks } from '../../hooks/useUpdateFeedbacks';

interface FieldsDataState {
    NameField: string;
    PhoneNumber: string;
}

export const ContactPage = () => {
    const feedback = useFeedbackStore((s) => s.feedback);
    const updatedFeedback = useFeedbackStore((s) => s.updateFeedback);
    const setUpdateFeedback = useFeedbackStore((s) => s.setUpdateFeedback);
    const setPageNumber = useFeedbackStore((s) => s.setPageNumber);
    const setNumberVisibility = useFeedbackStore((s) => s.setPageNumberVisible);
    const vm = useAppStore((s) => s.vm);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [notes, setNotes] = useState<FieldsDataState>({
        NameField: '',
        PhoneNumber: '',
    });
    const isNextButtonEnabled =
        notes.NameField.trim() !== '' ||
        notes.PhoneNumber.trim() !== '' ||
        COMPANY_IDS_FOR_FEEDBACKS.includes(vm.companyId);
    const nextButtonStyles = isNextButtonEnabled
        ? DISABLE_OR_ENABLE_BUTTON[0]
        : DISABLE_OR_ENABLE_BUTTON[1];

    const updateFeedbacksMutation = useUpdateFeedbacks();

    useEffect(() => {
        setPageNumber('3/3');
        setNumberVisibility(true);
    }, []);

    const handleNotesChange = (event: {
        target: { id: string; value: string };
    }) => {
        const { id, value } = event.target;
        setNotes({
            NameField: id === 'NameField' ? value : notes.NameField,
            PhoneNumber: id === 'PhoneNumber' ? value : notes.PhoneNumber,
        });
    };

    const onNextPage = async () => {
        const newFeedback: UpdateFeedback = {
            ...updatedFeedback,
            customer_status: FEEDBACKS_CUSTOMER_STATUS.AFTER_CONTACT_PAGE,
            customer_phone_number: notes.PhoneNumber
                ? notes.PhoneNumber
                : null,
            customer_name: notes.NameField ? notes.NameField : null,
        };
        setUpdateFeedback(newFeedback);
        try {
            await updateFeedbacksMutation.mutateAsync({ id: feedback.id, body: newFeedback });
        } catch (e) {
            debug(`Contact submit failed: ${String(e)}`, 'error');
        }
        setNumberVisibility(false);
        navigate(
            generatePath(FEEDBACK_ROUTES.complete, { id: vm.qr }),
        );
    };

    const onCancelClick = () => {
        setNumberVisibility(false);
        navigate(
            generatePath(FEEDBACK_ROUTES.complete, { id: vm.qr }),
            { replace: true },
        );
    };

    return (
        <ScreenWrapper>
            <ScreenTitle>{t('Your contact information')}</ScreenTitle>
            <ContentBox>
                <CustomTextField
                    id="NameField"
                    label={t('Name')}
                    onChange={handleNotesChange}
                    rows={1.5}
                />
                <CustomTextField
                    id="PhoneNumber"
                    label={t('Phone number')}
                    onChange={handleNotesChange}
                    rows={1.5}
                />

                <div className={styles.feedbacksButtonBox}>
                    <PrimaryButton
                        className={styles.feedbacksButton}
                        onClick={onCancelClick}
                    >
                        {t('Cancel')}
                    </PrimaryButton>
                    <PrimaryButton
                        className={styles.feedbacksButton}
                        onClick={onNextPage}
                        disabled={!isNextButtonEnabled || updateFeedbacksMutation.isPending}
                        loading={updateFeedbacksMutation.isPending}
                        style={nextButtonStyles}
                    >
                        {t('Send')}
                    </PrimaryButton>
                </div>
            </ContentBox>
        </ScreenWrapper>
    );
};
